import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';

const ResetPassword = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!token) {
      setError(language === 'en'
        ? 'Invalid reset link. Please request a new password reset.'
        : 'رابط إعادة التعيين غير صالح. يرجى طلب إعادة تعيين كلمة مرور جديدة.');
    }
  }, [token, language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(language === 'en'
        ? 'Passwords do not match'
        : 'كلمات المرور غير متطابقة');
      return;
    }

    if (formData.password.length < 6) {
      setError(language === 'en'
        ? 'Password must be at least 6 characters'
        : 'يجب أن تكون كلمة المرور 6 أحرف على الأقل');
      return;
    }

    if (!token) {
      setError(language === 'en'
        ? 'Invalid reset token'
        : 'رمز إعادة التعيين غير صالح');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        password: formData.password
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.error || (language === 'en'
        ? 'Failed to reset password. The link may have expired.'
        : 'فشل إعادة تعيين كلمة المرور. قد يكون الرابط منتهي الصلاحية.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-blackC flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 text-primary hover:text-primary-light transition-colors"
          >
            {language === 'en' ? 'Request New Reset Link' : 'طلب رابط إعادة تعيين جديد'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blackC flex items-center justify-center px-6 py-12">
      <div className={`w-full max-w-md transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-block">
            <span className="font-display text-4xl">
              <span className="text-primary">L</span>
              <span className="text-cream">imousine</span>
            </span>
          </Link>
        </div>

        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="font-display text-4xl text-cream mb-4">
            {language === 'en' ? 'Reset Password' : 'إعادة تعيين كلمة المرور'}
          </h1>
          <p className="text-gray-light">
            {language === 'en'
              ? 'Enter your new password below.'
              : 'أدخل كلمة المرور الجديدة أدناه.'}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-8 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-green-400 font-medium">
                  {language === 'en' ? 'Password Reset Successfully!' : 'تم إعادة تعيين كلمة المرور بنجاح!'}
                </p>
                <p className="text-green-400/70 text-sm mt-1">
                  {language === 'en'
                    ? 'Redirecting to login page...'
                    : 'جاري إعادة التوجيه إلى صفحة تسجيل الدخول...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-red-400 font-medium">
                  {language === 'en' ? 'Error' : 'خطأ'}
                </p>
                <p className="text-red-400/70 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'password' || formData.password
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {language === 'en' ? 'New Password' : 'كلمة المرور الجديدة'}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'password' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-14 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4a4a4a] hover:text-primary transition-colors p-1"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-primary-light transition-all duration-500 rounded-full ${focusedField === 'password' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Confirm Password Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'confirmPassword' || formData.confirmPassword
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {language === 'en' ? 'Confirm Password' : 'تأكيد كلمة المرور'}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'confirmPassword' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-14 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4a4a4a] hover:text-primary transition-colors p-1"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-primary-light transition-all duration-500 rounded-full ${focusedField === 'confirmPassword' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full group overflow-hidden"
            >
              <div className="relative bg-gradient-to-r from-primary to-primary-light text-blackC font-semibold py-4 px-6 flex items-center justify-center gap-3 rounded-lg transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                    {language === 'en' ? 'Resetting...' : 'جاري إعادة التعيين...'}
                  </>
                ) : (
                  <>
                    {language === 'en' ? 'Reset Password' : 'إعادة تعيين كلمة المرور'}
                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </div>
              <div className="absolute inset-0 bg-white/20 translate-x-[-100%] skew-x-12 group-hover:translate-x-[100%] transition-transform duration-700" />
            </button>
          </form>
        )}

        {/* Back to Login */}
        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-light hover:text-primary transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {language === 'en' ? 'Back to Login' : 'العودة لتسجيل الدخول'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

